<template>
	<div class="user-center">
		<section class="profile-card" v-loading="loading">
			<div class="profile-main">
				<img class="avatar" :src="user.avatar || '/img/avatar.jpg'" alt="avatar">
				<div class="profile-text">
					<h2>{{ user.nickname || user.username || '用户' }}</h2>
					<p class="username">@{{ user.username || '未知用户' }}</p>
					<p class="bio">{{ user.bio || '暂无个人简介' }}</p>
				</div>
			</div>
			<div class="profile-meta">
				<span>角色：{{ roleLabel(user.role) }}</span>
				<span>邮箱：{{ user.email || '-' }}</span>
				<span>注册时间：{{ formatDate(user.createTime) }}</span>
			</div>
		</section>

		<section class="stats-grid">
			<div class="stat-card">
				<strong>{{ stats.momentCount }}</strong>
				<span>已发布动态</span>
			</div>
			<div class="stat-card">
				<strong>{{ stats.commentCount }}</strong>
				<span>评论数</span>
			</div>
			<div class="stat-card">
				<strong>{{ stats.likeCount }}</strong>
				<span>点赞数</span>
			</div>
			<div class="stat-card">
				<strong>{{ stats.favoriteCount }}</strong>
				<span>收藏数</span>
			</div>
			<div class="stat-card">
				<strong>{{ formatDate(stats.lastLoginTime) }}</strong>
				<span>最近登录</span>
			</div>
		</section>

		<section class="record-grid">
			<div class="record-card">
				<div class="head">
					<h3>最近动态</h3>
				</div>
				<ul v-if="recent.moments.length > 0" class="record-list">
					<li v-for="item in recent.moments" :key="`moment-${item.id}`">
						<router-link :to="`/moments`">动态 #{{ item.id }}</router-link>
						<span>{{ formatDate(item.createTime) }}</span>
					</li>
				</ul>
				<EmptyState v-else text="暂无动态记录" icon-class="comment alternate outline icon" />
			</div>

			<div class="record-card">
				<div class="head">
					<h3>最近评论</h3>
				</div>
				<ul v-if="recent.comments.length > 0" class="record-list">
					<li v-for="item in recent.comments" :key="`comment-${item.id}`">
						<span class="content">{{ shortText(item.content) }}</span>
						<span>{{ formatDate(item.createTime) }}</span>
					</li>
				</ul>
				<EmptyState v-else text="暂无评论记录" icon-class="comments outline icon" />
			</div>

			<div class="record-card">
				<div class="head">
					<h3>最近点赞</h3>
				</div>
				<ul v-if="recent.likes.length > 0" class="record-list">
					<li v-for="item in recent.likes" :key="`like-${item.id}`">
						<router-link to="/moments">{{ item.label }}</router-link>
					</li>
				</ul>
				<EmptyState v-else text="暂无点赞记录" icon-class="heart outline icon" />
			</div>
		</section>
	</div>
</template>

<script>
import EmptyState from '@/components/user/EmptyState.vue'
import {fetchUserCenterData} from '@/api/user'

export default {
	name: 'UserCenter',
	components: {EmptyState},
	data() {
		return {
			loading: false,
			user: {},
			stats: {
				momentCount: 0,
				commentCount: 0,
				likeCount: 0,
				favoriteCount: 0,
				lastLoginTime: ''
			},
			recent: {
				moments: [],
				comments: [],
				likes: []
			}
		}
	},
	created() {
		this.loadData()
	},
	methods: {
		loadData() {
			this.loading = true
			fetchUserCenterData().then(data => {
				this.user = data.user || {}
				this.stats = data.stats || this.stats
				this.recent = data.recent || this.recent
			}).catch(() => {
				this.msgError('个人中心数据加载失败')
			}).finally(() => {
				this.loading = false
			})
		},
		roleLabel(role) {
			if (role === 'ROLE_admin') {
				return '管理员'
			}
			return '普通用户'
		},
		formatDate(value) {
			if (!value) {
				return '-'
			}
			const date = new Date(value)
			if (isNaN(date.getTime())) {
				return String(value)
			}
			const yyyy = date.getFullYear()
			const mm = String(date.getMonth() + 1).padStart(2, '0')
			const dd = String(date.getDate()).padStart(2, '0')
			const hh = String(date.getHours()).padStart(2, '0')
			const ii = String(date.getMinutes()).padStart(2, '0')
			return `${yyyy}-${mm}-${dd} ${hh}:${ii}`
		},
		shortText(text = '') {
			const plain = text.replace(/<[^>]+>/g, '').trim()
			if (plain.length <= 48) {
				return plain
			}
			return `${plain.slice(0, 48)}...`
		}
	}
}
</script>

<style scoped>
.user-center {
	display: flex;
	flex-direction: column;
	gap: 14px;
}

.profile-card {
	background: #fff;
	border: 1px solid #e7eef5;
	border-radius: 16px;
	box-shadow: 0 10px 28px rgba(15, 23, 42, .06);
	padding: 16px;
}

.profile-main {
	display: flex;
	gap: 14px;
	align-items: center;
}

.avatar {
	width: 72px;
	height: 72px;
	border-radius: 50%;
	object-fit: cover;
	border: 2px solid #dbeafe;
}

.profile-text h2 {
	margin: 0;
	font-size: 22px;
	color: #0f172a;
}

.profile-text .username {
	margin: 4px 0;
	font-size: 13px;
	color: #475569;
}

.profile-text .bio {
	margin: 0;
	font-size: 13px;
	color: #64748b;
}

.profile-meta {
	margin-top: 10px;
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	font-size: 12px;
	color: #475569;
}

.profile-meta span {
	background: #f8fbff;
	border: 1px solid #e2e8f0;
	padding: 5px 8px;
	border-radius: 999px;
}

.stats-grid {
	display: grid;
	grid-template-columns: repeat(5, minmax(0, 1fr));
	gap: 10px;
}

.stat-card {
	background: #fff;
	border: 1px solid #e7eef5;
	border-radius: 12px;
	padding: 10px;
	text-align: center;
}

.stat-card strong {
	display: block;
	font-size: 18px;
	color: #0f172a;
}

.stat-card span {
	display: block;
	margin-top: 4px;
	font-size: 12px;
	color: #64748b;
}

.record-grid {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 10px;
}

.record-card {
	background: #fff;
	border: 1px solid #e7eef5;
	border-radius: 12px;
	padding: 10px;
	min-height: 190px;
}

.record-card .head h3 {
	margin: 0;
	font-size: 14px;
	color: #0f172a;
}

.record-list {
	margin: 10px 0 0;
	padding: 0;
	list-style: none;
	display: flex;
	flex-direction: column;
	gap: 8px;
	max-height: 180px;
	overflow: auto;
}

.record-list li {
	display: flex;
	flex-direction: column;
	gap: 3px;
	padding: 7px;
	border-radius: 8px;
	background: #f8fafc;
	border: 1px solid #edf2f7;
	font-size: 12px;
	color: #475569;
	word-break: break-word;
}

.record-list .content {
	color: #1f2937;
}

@media (max-width: 992px) {
	.stats-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.record-grid {
		grid-template-columns: 1fr;
	}
}
</style>
